import {useEffect, useRef, useState} from "react";
import {OrdersListDTO} from "@dto/OrdersListDTO";
import {
    joinLeaderElection,
    leaveLeaderElection,
    subscribeToOrdersLists
} from "@features/orders-poller/orders-poller";

/**
 * Whether this tab polls the orders list of the key. null: no polling (e.g. the archive), no election.
 * False until the election ran, so a follower tab never starts a poll timer.
 */
export const useOrdersPollerLeadership = (pollerKey: string | null): boolean => {
    const [isLeader, setIsLeader] = useState(false)

    useEffect(() => {
        if (pollerKey === null) {
            setIsLeader(false)
            return
        }

        const election = joinLeaderElection(pollerKey)
        setIsLeader(election.isLeader())
        const unsubscribe = election.subscribe(() => setIsLeader(election.isLeader()))

        return () => {
            unsubscribe()
            leaveLeaderElection(pollerKey)
        }
    }, [pollerKey])

    return isLeader
}

/**
 * Lists fetched by the other tabs of the browser with the same key.
 */
export const useOrdersPollerUpdates = (
    pollerKey: string | null,
    onOrdersList: (ordersList: OrdersListDTO) => void,
): void => {
    // The latest callback, without subscribing again on every render.
    const onOrdersListRef = useRef(onOrdersList)
    onOrdersListRef.current = onOrdersList

    useEffect(() => {
        if (pollerKey === null) return

        return subscribeToOrdersLists(pollerKey, ordersList => onOrdersListRef.current(ordersList))
    }, [pollerKey])
}
